/**
 * openReceiptPrint — abre um recibo em nova janela pronto para impressão.
 * data = { title, subtitle, item, sn, technician, date, signature, photos, meta: [{label,value}] }
 */
export function openReceiptPrint(data) {
  const metaRows = (data.meta || [])
    .map((m) => `<tr><td class="k">${escapeHtml(m.label)}</td><td class="v">${escapeHtml(m.value ?? "-")}</td></tr>`)
    .join("");
  const photos = (data.photos || [])
    .filter((p) => isSafeImageDataUrl(p))
    .map((p) => `<img src="${escapeAttr(p)}" alt="foto" />`)
    .join("");
  const safeSignature = isSafeImageDataUrl(data.signature) ? data.signature : "";
  const html = `
<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Recibo · ${escapeHtml(data.title || "")}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #111; margin: 0; padding: 40px; }
  h1 { font-size: 22px; margin: 0 0 4px; letter-spacing: 0.02em; }
  h2 { font-size: 14px; font-weight: 500; color: #555; margin: 0 0 24px; }
  .box { border: 1px solid #ddd; padding: 18px; margin-bottom: 18px; }
  .label { text-transform: uppercase; font-size: 10px; letter-spacing: 0.14em; color: #777; margin-bottom: 4px; }
  .big { font-size: 18px; font-weight: 700; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  td { padding: 6px 4px; border-bottom: 1px solid #eee; }
  td.k { color: #666; text-transform: uppercase; font-size: 10px; letter-spacing: 0.1em; width: 40%; }
  td.v { color: #111; font-weight: 500; }
  .sig-box { border: 1px solid #ddd; padding: 12px; text-align: center; margin-top: 8px; }
  .sig-box img { max-height: 120px; }
  .photos { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; }
  .photos img { width: 100%; height: 160px; object-fit: cover; border: 1px solid #ddd; }
  .footer { margin-top: 30px; font-size: 11px; color: #888; text-align: center; }
  .brand { display: flex; align-items: center; gap: 10px; margin-bottom: 24px; }
  .brand-name { font-weight: 700; font-size: 16px; letter-spacing: 0.1em; }
  @media print { body { padding: 20px; } .no-print { display: none !important; } }
</style>
</head>
<body>
  <div class="brand">
    <div class="brand-name">DBS FIELD</div>
    <div style="color:#888;font-size:12px">Gestão de Campo</div>
  </div>

  <h1>${escapeHtml(data.title || "Recibo de Retirada")}</h1>
  <h2>${escapeHtml(data.subtitle || "")}</h2>

  <div class="box">
    <div class="label">Item</div>
    <div class="big">${escapeHtml(data.item || "-")}</div>
    ${data.sn ? `<div style="font-family: 'Courier New', monospace; margin-top: 4px; color: #444">SN: <b>${escapeHtml(data.sn)}</b></div>` : ""}
  </div>

  <div class="box">
    <table>
      <tr><td class="k">Técnico</td><td class="v">${escapeHtml(data.technician || "-")}</td></tr>
      <tr><td class="k">Data</td><td class="v">${escapeHtml(data.date || "-")}</td></tr>
      ${metaRows}
    </table>
  </div>

  ${safeSignature ? `
  <div class="box">
    <div class="label">Assinatura do Técnico</div>
    <div class="sig-box"><img src="${escapeAttr(safeSignature)}" alt="assinatura" /></div>
  </div>` : ""}

  ${photos ? `
  <div class="box">
    <div class="label">Fotos do Estado</div>
    <div class="photos">${photos}</div>
  </div>` : ""}

  <div class="footer">Documento gerado automaticamente pela plataforma DBS FIELD</div>

  <div class="no-print" style="margin-top: 20px; text-align: center;">
    <button onclick="window.print()" style="padding: 10px 24px; background: #0EA5E9; color: white; border: none; font-weight: 600; cursor: pointer; letter-spacing: 0.05em;">IMPRIMIR / SALVAR PDF</button>
  </div>
</body>
</html>`;
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank", "width=820,height=1000");
  if (!win) {
    URL.revokeObjectURL(url);
    return;
  }
  // Revoke the Blob URL after the window has had a chance to load it,
  // to avoid leaking memory. The printed document keeps working.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function escapeHtml(s) {
  if (s == null) return "";
  return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

// Same escape as HTML — safe for use inside HTML attribute values.
function escapeAttr(s) {
  return escapeHtml(s);
}

// Whitelist only inline base64/URL-encoded image data URLs (png/jpg/jpeg/webp/gif/svg).
// Blocks any `javascript:` / cross-origin URL that could execute code or leak referrers.
function isSafeImageDataUrl(s) {
  if (!s || typeof s !== "string") return false;
  return /^data:image\/(png|jpe?g|webp|gif|svg\+xml);(base64|utf8|charset=[a-z0-9-]+),/i.test(s.trim());
}
